import { Button, DrawerBody, DrawerFooter, DrawerHeader, Heading, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getTenantId } from '@pelicargo/tenants';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { structuredAddressSchema, StructuredAddress } from '@pelicargo/utils';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { BaseDrawer } from '../BaseDrawer';
import { ContactForm } from '../Forms/ContactForm';
import { SimpleForm } from '../SimpleForm';

export type FormValues = {
  preferred_name: string;
  full_name: string;
  email: string;
  phone_number: string;
  notes: string;
  title: string;
  address: StructuredAddress | null;
  user_id: number;

  associations: {
    accounts: number[];
    providers: string[];
    airports: string[];
    tags: string[];
    id?: number;
  }[];
};

const schema = z.object({
  preferred_name: z.string().min(1, 'Required'),
  full_name: z.string().min(1, 'Required'),
  email: z.string().email(),
  phone_number: z.string().nullable(),
  notes: z.string().nullable(),
  title: z.string().nullable(),
  address: structuredAddressSchema.nullish(),
  user_id: z.coerce.number().nullish(),
  tenants: z.array(z.string()).min(1).default([getTenantId()]),
  is_impersonation_contact: z.boolean().default(false),

  associations: z.array(
    z.object({
      id: z.coerce.number().nullish(),
      accounts: z.array(z.coerce.number()).nullish(),
      providers: z.array(z.string()).nullish(),
      airports: z.array(z.string()).nullish(),
      tags: z.array(z.string()).nullish(),
    }),
  ),
});

type Props = {
  contactId?: number | null;
  isOpen?: boolean;
  onClose?: () => void;
  onUpdate?: (contact: TrpcOutputs['updateContact']['iContact']) => void;
  onContactCreate?: any;
  initialState?: Partial<z.infer<typeof schema>>;
};

export const ContactFormDrawer = ({ contactId, isOpen, onClose, onUpdate, onContactCreate, initialState }: Props) => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const methods = useForm({ resolver: zodResolver(schema) });
  const { data, refetch } = trpc.getContact.useQuery({ id: contactId as number }, { enabled: !!contactId });
  const { data: currentTenant, refetch: refetchTenant } = trpc.getCurrentTenant.useQuery();

  const { mutateAsync: createContact, isLoading: isCreating } = trpc.createContact.useMutation();
  const { mutateAsync: updateContact, isLoading: isUpdating } = trpc.updateContact.useMutation();

  useEffect(() => {
    if (!contactId) {
      methods.reset({
        preferred_name: initialState?.preferred_name ?? '',
        full_name: initialState?.full_name ?? '',
        email: initialState?.email ?? '',
        phone_number: initialState?.phone_number ?? '',
        notes: initialState?.notes ?? '',
        title: initialState?.title ?? '',
        address: initialState?.address ?? undefined,
        associations: initialState?.associations ?? [],
        user_id: initialState?.user_id ?? '',
        tenants: initialState?.tenants ?? [getTenantId()],
      });
    }
    if (contactId) {
      methods.reset({
        preferred_name: data?.preferred_name,
        full_name: data?.full_name,
        email: data?.email,
        phone_number: data?.phone_number,
        tags: data?.tags ?? [],
        notes: data?.notes,
        title: data?.title,
        address: data?.address,
        is_impersonation_contact: currentTenant?.impersonation_contact_id === data?.id,
        associations:
          data?.associations?.map((association) => ({
            providers: association.providers?.map((x) => x.id),
            airports: association.airports?.map((x) => x.id),
            accounts: association.accounts?.map((x) => x.id),
            tags: association.tags,
            associationId: association.id,
          })) ?? [],
        user_id: data?.user?.id ?? 0,
        tenants: data?.tenants?.map(({ id }) => id) ?? [getTenantId()],
      });
    }
  }, [data, contactId, methods, initialState, currentTenant?.impersonation_contact_id]);

  const copy = useMemo(() => {
    return contactId ? 'Update Contact' : 'Create Contact';
  }, [contactId]);

  const onValid = async (values: FormValues) => {
    try {
      if (contactId) {
        const { iContact } = await updateContact({
          ...values,
          id: contactId,
        });

        onUpdate?.(iContact);
      }
      handleSuccess(contactId ? 'Contact updated' : 'Contact created');
      if (!contactId) {
        const contact = await createContact(values);
        onContactCreate?.(contact);
      }
      refetchTenant();
      refetch();
      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  const handleClose = () => {
    methods.reset();
    onClose?.();
  };

  return (
    <BaseDrawer isOpen={isOpen} onClose={handleClose}>
      <SimpleForm {...methods} submitOnBlur={false}>
        <form
          onSubmit={methods.handleSubmit(onValid)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <DrawerHeader>
            <Heading variant="h4">{copy}</Heading>
          </DrawerHeader>
          <DrawerBody>
            <ContactForm />
          </DrawerBody>
          <DrawerFooter>
            <Stack direction="row" justify={contactId ? 'space-between' : 'right'} align="center" w="full" spacing="2">
              <Show if={!!contactId}>
                <Text>ID: {contactId}</Text>
              </Show>
              <Button type="submit" colorScheme="brand" isLoading={isCreating || isUpdating}>
                {copy}
              </Button>
            </Stack>
          </DrawerFooter>
        </form>
      </SimpleForm>
    </BaseDrawer>
  );
};
